import { ACTIVATION_LABELS } from '../classification';
import { FOCUSED_REASON_LABELS } from '../bubble/ancestryChips';
import { truncate } from '../formatBytes';
import type { ExplorationSecondary, ExplorationVolume, ViewMode } from '../../types/exploration';

/** Fits ~2 truncated segments plus entity tag within the breadcrumb nav. */
export const BREADCRUMB_LABEL_MAX = 28;

export type BreadcrumbDisplayItem =
  | { kind: 'segment'; name: string; drillIndex: number }
  | { kind: 'collapse'; hiddenNames: string[] };

export type BreadcrumbSegment = { name: string; drillIndex: number };

export interface BreadcrumbCompactOptions {
  secondary: ExplorationSecondary;
  view: ViewMode;
}

export interface BreadcrumbStatusTagOptions extends BreadcrumbCompactOptions {
  volumes?: ExplorationVolume[];
}

const EXPLORATION_TOP_LEVEL_LABELS = new Set(['Supported', 'Unsupported', 'Unattached']);
const ACTIVATION_TOP_LEVEL_LABELS = new Set<string>(Object.values(ACTIVATION_LABELS));

export function isTopLevelStatusLabel(name: string, view: ViewMode): boolean {
  if (view === 'activation') return ACTIVATION_TOP_LEVEL_LABELS.has(name);
  return EXPLORATION_TOP_LEVEL_LABELS.has(name);
}

export function topLevelStatusTagForVolume(v: ExplorationVolume, view: ViewMode): string {
  if (view === 'activation') return v.activationLabel;
  if (v.supportStatus === 'supported') return 'Supported';
  if (v.supportStatus === 'unattached') return 'Unattached';
  return 'Unsupported';
}

// Reason text now comes straight from the backend (no curated/enumerable set of known
// reasons on the frontend anymore), so there's no fixed universe to check `name` against.
// Instead, treat `name` as a reason label when it matches the current reason of the
// already-scoped volumes passed in for this breadcrumb tail.
function isReasonLabel(name: string, volumes?: ExplorationVolume[]): boolean {
  return !!volumes?.length && volumes[0]!.supportLabel === name;
}

/** Top-level status category for the breadcrumb tail tag (never a reason name). */
export function breadcrumbStatusTagLabel(
  drillPath: string[],
  opts: BreadcrumbStatusTagOptions
): string | null {
  if (!drillPath.length) return null;

  const tail = drillPath[drillPath.length - 1];

  if (opts.view === 'activation') {
    const focusedIdx = drillPath.findIndex((segment) => FOCUSED_REASON_LABELS.has(segment));
    if (focusedIdx !== -1) {
      return drillPath[focusedIdx];
    }
  }

  if (isTopLevelStatusLabel(tail, opts.view)) {
    return tail;
  }

  if (isReasonLabel(tail, opts.volumes)) {
    return topLevelStatusTagForVolume(opts.volumes![0]!, opts.view);
  }

  return null;
}

/** Compact activation drill paths for breadcrumb display. */
export function compactDrillPathForBreadcrumb(
  drillPath: string[],
  opts: BreadcrumbCompactOptions
): BreadcrumbSegment[] {
  let segments = drillPath.map((name, drillIndex) => ({ name, drillIndex }));
  if (opts.view !== 'activation') return segments;

  const focusedIdx = drillPath.findIndex((segment) => FOCUSED_REASON_LABELS.has(segment));
  if (focusedIdx === -1) return segments;

  const tail = drillPath[drillPath.length - 1];
  const hasReasonTail = drillPath.length > focusedIdx + 1 && !FOCUSED_REASON_LABELS.has(tail);

  if (opts.secondary === 'cluster') {
    segments = segments.filter((segment) => segment.name !== 'EC2 Instances');
  }

  if (opts.secondary === 'region' || opts.secondary === 'tag') {
    if (hasReasonTail) {
      segments = segments.filter((segment) => segment.drillIndex !== focusedIdx);
    }
  }

  return segments;
}

export function segmentsToDisplayItems(segments: BreadcrumbSegment[]): BreadcrumbDisplayItem[] {
  if (segments.length <= 2) {
    return segments.map((segment) => ({
      kind: 'segment',
      name: segment.name,
      drillIndex: segment.drillIndex,
    }));
  }
  const hiddenNames = segments.slice(0, -2).map((segment) => segment.name);
  const tail = segments.slice(-2);
  return [
    { kind: 'collapse', hiddenNames },
    { kind: 'segment', name: tail[0].name, drillIndex: tail[0].drillIndex },
    { kind: 'segment', name: tail[1].name, drillIndex: tail[1].drillIndex },
  ];
}

export function buildBreadcrumbDisplay(segments: string[]): BreadcrumbDisplayItem[] {
  return segmentsToDisplayItems(segments.map((name, drillIndex) => ({ name, drillIndex })));
}

export function getBreadcrumbTailDrillIndex(items: BreadcrumbDisplayItem[]): number | null {
  const tailItem = [...items]
    .reverse()
    .find((item): item is Extract<BreadcrumbDisplayItem, { kind: 'segment' }> => item.kind === 'segment');
  return tailItem?.drillIndex ?? null;
}

export function formatBreadcrumbLabel(name: string): string {
  return truncate(name, BREADCRUMB_LABEL_MAX);
}
